import {
  Home,
  BookOpen,
  Ticket,
  LayoutDashboard,
  User,
  Shield,
  GraduationCap,
  Star,
  type LucideIcon,
} from 'lucide-react'
import type { ClientUiConfig, TabKey, TabIcon, SectionKey, PageKey } from '@/types/api'

/**
 * 用戶端 UI 編排：白名單註冊表。
 * admin 只能「組合」這裡註冊過的項目——路徑與元件由前端決定，
 * 配置只攜帶 key/label/icon/順序/開關，不可能注入任意內容。
 */

// tab key → 路徑（固定，不入配置）
export const TAB_PATHS: Record<TabKey, string> = {
  home: '/',
  courses: '/courses',
  enrollments: '/my/enrollments',
  dashboard: '/dashboard',
  profile: '/student/profile',
  admin: '/admin/dashboard',
}

export const ICON_MAP: Record<TabIcon, LucideIcon> = {
  home: Home,
  'book-open': BookOpen,
  ticket: Ticket,
  'layout-dashboard': LayoutDashboard,
  user: User,
  shield: Shield,
  'graduation-cap': GraduationCap,
  star: Star,
}

export const SECTION_LABELS: Record<SectionKey, string> = {
  hero: '主視覺（標題區）',
  quick_stats: '快速統計',
  featured_courses: '精選課程',
  features: '平台特色',
  cta_banner: '行動呼籲橫幅',
}

export const PAGE_LABELS: Record<PageKey, string> = {
  courses: '課程列表/詳情',
  dashboard: '教學儀表板',
  profile: '個人資料',
  enrollments: '我的報名',
}

// 與後端 DEFAULT_CLIENT_UI 同步的前端後備（API 失敗時仍能渲染完整 app）
export const DEFAULT_CLIENT_UI: ClientUiConfig = {
  version: 0,
  tabbar: [
    { key: 'home', label: '首頁', icon: 'home', enabled: true, roles: ['student', 'teacher', 'admin'], order: 1 },
    { key: 'courses', label: '課程', icon: 'book-open', enabled: true, roles: ['student', 'teacher', 'admin'], order: 2 },
    { key: 'enrollments', label: '報名', icon: 'ticket', enabled: true, roles: ['student'], order: 3 },
    { key: 'dashboard', label: '儀表板', icon: 'layout-dashboard', enabled: true, roles: ['teacher', 'admin'], order: 3 },
    { key: 'admin', label: '管理', icon: 'shield', enabled: true, roles: ['admin'], order: 4 },
    { key: 'profile', label: '我的', icon: 'user', enabled: true, roles: ['student', 'teacher', 'admin'], order: 5 },
  ],
  pages: {
    courses: { enabled: true },
    dashboard: { enabled: true },
    profile: { enabled: true },
    enrollments: { enabled: true },
  },
  home: {
    sections: [
      { key: 'hero', enabled: true, order: 1, props: { title: 'CF學習網', subtitle: '直接在 Telegram 中學習和分享知識' } },
      { key: 'quick_stats', enabled: true, order: 2, props: {} },
      { key: 'featured_courses', enabled: true, order: 3, props: { title: '精選課程', limit: 6 } },
      { key: 'features', enabled: true, order: 4, props: {} },
      { key: 'cta_banner', enabled: false, order: 5, props: { text: '', buttonLabel: '瀏覽課程' } },
    ],
  },
}

/** 依角色取得目前應顯示的 tab（enabled + 角色符合 + order 排序 + 上限 5） */
export function resolveTabsForRole(config: ClientUiConfig, role?: string) {
  const effectiveRole = role === 'admin' || role === 'teacher' ? role : 'student'
  return config.tabbar
    .filter(tab => tab.enabled && tab.roles.includes(effectiveRole as 'student' | 'teacher' | 'admin'))
    .sort((a, b) => a.order - b.order)
    .slice(0, 5)
}
